import React, { Component, useEffect } from "react";
import { Link, withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Underline } from "@tiptap/extension-underline";
import { Highlight } from "@tiptap/extension-highlight";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { TextAlign } from "@tiptap/extension-text-align";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Mention } from "@tiptap/extension-mention";
import { Mathematics } from "@tiptap/extension-mathematics";
import { Emoji } from "@tiptap/extension-emoji";
import { Details, DetailsSummary, DetailsContent } from "@tiptap/extension-details";
import { createLowlight, all } from "lowlight";
import ImageWithSize from "./extensions/ImageWithSize";
import CodeBlockWithCaption from "./extensions/CodeBlockWithCaption";
import Iframe from "./extensions/Iframe";
import "katex/dist/katex.min.css";
import {
  FileText, Calendar, User, Tag, Eye, Pencil, ArrowLeft,
  CircleDot, Mail, Send,
  Github, Gitlab, Facebook, Twitter, Linkedin, Instagram, Youtube,
  Dribbble, PenTool, Code, MessageSquare, Globe, Link as LinkIcon,
} from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import { formatDateTime } from "../../utils/dateUtils";
import BehanceIcon from "./icons/BehanceIcon";
import SafeImage from "../shared/SafeImage";
import { attachImageFallback } from "../../utils/imageFallback";

import {
  PageHeader,
  Card,
  Button,
  Spinner,
  EmptyState,
  DescriptionList,
  Modal,
  FormField,
  Input,
  ArticleStatusBadge,
  ArticleFeaturedBadge,
  ArticleTypeLabel,
} from "../../design-system";

const lowlight = createLowlight(all);

/* -------------------------------------------------------------------------- */
/*  ReadOnlyEditor — TipTap en lecture seule                                  */
/* -------------------------------------------------------------------------- */

const ReadOnlyEditor = ({ content }) => {
  let parsedContent = content;
  if (typeof content === "string" && content.trim() !== "") {
    try {
      parsedContent = JSON.parse(content);
    } catch {
      parsedContent = { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: content }] }] };
    }
  }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      LinkExtension.configure({ openOnClick: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image,
      ImageWithSize,
      CodeBlockWithCaption.configure({ lowlight }),
      Iframe,
      Table.configure({ resizable: false }),
      TableRow, TableCell, TableHeader,
      Underline,
      Highlight.configure({ multicolor: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Subscript, Superscript,
      Mention.configure({ HTMLAttributes: { class: "mention" } }),
      Mathematics,
      Emoji,
      Details, DetailsSummary, DetailsContent,
    ],
    content: parsedContent || "",
    editable: false,
  });

  useEffect(() => {
    if (editor && content !== undefined) {
      let newContent;
      if (typeof content === "string" && content.trim() !== "") {
        try {
          newContent = JSON.parse(content);
        } catch {
          newContent = { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: content }] }] };
        }
      } else {
        newContent = content;
      }
      const currentStr = JSON.stringify(editor.getJSON());
      const newStr = JSON.stringify(newContent || "");
      if (currentStr !== newStr) editor.commands.setContent(newContent || "");
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return undefined;
    return attachImageFallback(editor.view.dom);
  }, [editor]);

  if (!editor) return <div className="text-mute text-sm">Chargement du contenu…</div>;
  return <div className="tiptap-readonly prose-sm max-w-none"><EditorContent editor={editor} /></div>;
};

/* -------------------------------------------------------------------------- */
/*  Helpers liens sociaux                                                     */
/* -------------------------------------------------------------------------- */

const SOCIAL_ICONS = {
  github: Github, gitlab: Gitlab, facebook: Facebook, twitter: Twitter,
  linkedin: Linkedin, instagram: Instagram, youtube: Youtube, dribbble: Dribbble,
  medium: PenTool, devto: Code, codepen: Code, stackoverflow: MessageSquare,
  reddit: MessageSquare, discord: MessageSquare, telegram: MessageSquare,
  whatsapp: MessageSquare, mastodon: MessageSquare, website: Globe,
};

const SOCIAL_LABELS = {
  link: "Lien", github: "GitHub", gitlab: "GitLab", facebook: "Facebook",
  twitter: "Twitter", linkedin: "LinkedIn", instagram: "Instagram", youtube: "YouTube",
  dribbble: "Dribbble", behance: "Behance", medium: "Medium", devto: "Dev.to",
  codepen: "CodePen", stackoverflow: "Stack Overflow", reddit: "Reddit",
  discord: "Discord", telegram: "Telegram", whatsapp: "WhatsApp", mastodon: "Mastodon",
  website: "Site web",
};

function SocialLinkChip({ type, url }) {
  const Icon = type === "behance" ? () => <BehanceIcon size={14} /> : (SOCIAL_ICONS[type] || LinkIcon);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-surface hover:bg-bg-2 transition-colors text-sm text-text"
    >
      <Icon className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
      <span>{SOCIAL_LABELS[type] || "Lien"}</span>
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/*  ArticleDetail                                                             */
/* -------------------------------------------------------------------------- */

class ArticleDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      article: null,
      loading: true,
      showTestModal: false,
      testEmail: "",
      testing: false,
    };
  }

  componentDidMount() {
    this.loadArticle();
  }

  loadArticle = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const { id } = this.props.match.params;
      const response = await axios.get(`/api/articles/id/${id}`);
      this.setState({ article: response.data.article, loading: false });
    } catch (error) {
      console.error("Error loading article:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible de charger l'article",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      if (error.response?.status === 404) {
        setTimeout(() => this.props.history.push("/dashboard/articles"), 2000);
      }
    }
  };

  handleOpenTestModal = () => this.setState({ showTestModal: true });
  handleCloseTestModal = () => this.setState({ showTestModal: false, testEmail: "", testing: false });

  handleTestNewsletter = async () => {
    const { testEmail, article } = this.state;
    if (!testEmail || !testEmail.includes("@")) {
      Store.addNotification({
        title: "Erreur", message: "Email invalide",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      return;
    }
    try {
      this.setState({ testing: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.post("/api/newsletters/test", { testEmail, articleId: article.id });
      if (response.data.success) {
        Store.addNotification({
          title: "Test envoyé",
          message: response.data.message || `Email envoyé à ${testEmail}.`,
          type: "success", insert: "top", container: "top-right", dismiss: { duration: 5000 },
        });
        this.handleCloseTestModal();
      }
    } catch (error) {
      console.error("Error testing newsletter:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible d'envoyer le test",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
    } finally {
      this.setState({ testing: false });
    }
  };

  render() {
    const { article, loading, showTestModal, testing, testEmail } = this.state;

    if (loading) {
      return (
        <AdminPageShell pageTitle="Article">
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    if (!article) {
      return (
        <AdminPageShell pageTitle="Article">
          <EmptyState
            title="Article non trouvé"
            description="L'article demandé n'existe pas ou a été supprimé."
            action={<Button as={Link} to="/dashboard/articles">Retour à la liste</Button>}
          />
        </AdminPageShell>
      );
    }

    const socialLinks = (() => {
      try { return article.socialLinks ? JSON.parse(article.socialLinks) : []; }
      catch { return []; }
    })();

    return (
      <AdminPageShell pageTitle={article.title} breadcrumbLast={article.title}>
        <PageHeader
          size="hero"
          eyebrow={`— Article · ${article.type || "—"}`}
          title={article.title}
          description={article.excerpt ? <span className="font-serif italic">— {article.excerpt}</span> : null}
          badge={
            <div className="flex items-center gap-2 flex-wrap">
              {article.featured && <ArticleFeaturedBadge />}
              <ArticleStatusBadge status={article.status} />
            </div>
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                as={Link}
                to="/dashboard/articles"
                variant="secondary"
                leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
              >
                Liste
              </Button>
              {article.status === "PUBLISHED" && (
                <Button
                  variant="secondary"
                  leftIcon={<Send className="w-4 h-4" strokeWidth={1.8} />}
                  onClick={this.handleOpenTestModal}
                >
                  Tester newsletter
                </Button>
              )}
              <Button
                as={Link}
                to={`/dashboard/articles/${article.id}`}
                leftIcon={<Pencil className="w-4 h-4" strokeWidth={1.8} />}
              >
                Modifier
              </Button>
            </div>
          }
        />

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
          {/* Colonne principale — contenu */}
          <div className="xl:col-span-3 space-y-5 min-w-0">
            {article.imageUrl && (
              <Card padding="none" className="overflow-hidden">
                <SafeImage
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full max-h-[480px] object-cover"
                />
              </Card>
            )}

            <Card>
              <Card.Header>
                <Card.Title>Contenu</Card.Title>
              </Card.Header>
              <ReadOnlyEditor content={article.content} />
            </Card>

            {(article.metaTitle || article.metaDescription) && (
              <Card>
                <Card.Header>
                  <Card.Title>Métadonnées SEO</Card.Title>
                </Card.Header>
                <DescriptionList orientation="vertical" items={[
                  ...(article.metaTitle ? [{ term: "Titre SEO", description: article.metaTitle }] : []),
                  ...(article.metaDescription ? [{ term: "Description SEO", description: article.metaDescription }] : []),
                ]} />
              </Card>
            )}

            {socialLinks.length > 0 && (
              <Card>
                <Card.Header>
                  <Card.Title>Liens externes</Card.Title>
                </Card.Header>
                <div className="flex flex-wrap gap-2">
                  {socialLinks.map((link, i) => (
                    <SocialLinkChip key={i} type={link.type} url={link.url} />
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Colonne latérale — infos */}
          <div className="space-y-5">
            <Card>
              <Card.Header>
                <Card.Title>Informations</Card.Title>
              </Card.Header>
              <DescriptionList orientation="vertical" items={[
                {
                  term: "Type",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <ArticleTypeLabel type={article.type} />
                    </span>
                  ),
                },
                {
                  term: "Statut",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <CircleDot className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <ArticleStatusBadge status={article.status} size="sm" />
                    </span>
                  ),
                },
                {
                  term: "Auteur",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      {article.author?.name || article.author?.email || "—"}
                    </span>
                  ),
                },
                ...(article.slug ? [{
                  term: "Slug",
                  description: (
                    <code className="font-mono text-xs px-1.5 py-0.5 rounded bg-bg-2 border border-border">
                      {article.slug}
                    </code>
                  ),
                }] : []),
                {
                  term: "Créé le",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <span className="font-mono text-xs">{formatDateTime(article.createdAt)}</span>
                    </span>
                  ),
                },
                ...(article.publishedAt ? [{
                  term: "Publié le",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <span className="font-mono text-xs">{formatDateTime(article.publishedAt)}</span>
                    </span>
                  ),
                }] : []),
                ...(article.color ? [{
                  term: "Couleur",
                  description: (
                    <span className="inline-flex items-center gap-2">
                      <span className="w-5 h-5 rounded border border-border" style={{ background: article.color }} />
                      <code className="font-mono text-xs">{article.color}</code>
                    </span>
                  ),
                }] : []),
              ]} />
            </Card>

            {article.excerpt && (
              <Card variant="filled">
                <Card.Header>
                  <Card.Title className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4" strokeWidth={1.8} />Extrait
                  </Card.Title>
                </Card.Header>
                <p className="text-sm text-text-2 leading-relaxed">{article.excerpt}</p>
              </Card>
            )}
          </div>
        </div>

        {/* Modal de test newsletter */}
        <Modal
          isOpen={showTestModal}
          onClose={this.handleCloseTestModal}
          size="md"
          title="Tester l'envoi de newsletter"
          description={`Article : « ${article.title} »`}
          footer={
            <>
              <Button variant="secondary" onClick={this.handleCloseTestModal} isDisabled={testing}>
                Annuler
              </Button>
              <Button
                onClick={this.handleTestNewsletter}
                isLoading={testing}
                isDisabled={!testEmail}
                leftIcon={<Mail className="w-4 h-4" strokeWidth={1.8} />}
              >
                Envoyer le test
              </Button>
            </>
          }
        >
          <FormField
            label="Email de test"
            required
            helper="L'email sera ajouté à la liste des abonnés s'il n'existe pas déjà."
          >
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => this.setState({ testEmail: e.target.value })}
              placeholder="vous@exemple.fr"
              autoFocus
            />
          </FormField>
        </Modal>
      </AdminPageShell>
    );
  }
}

export default withRouter(ArticleDetail);
